
import React, { useState, useContext, useEffect } from 'react'
import {SpinnerComponent} from 'react-element-spinner'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Container } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'

// import SimpleHeader from '../../../components/Headers/SimpleHeader'
// import EmptyHeader from '../../../components/Headers/EmptyHeader'
import { AuctionsContext } from '../../../contexts/AuctionsProvider'
import { SessionContext } from '../../../contexts/SessionProvider'
import AuctionsService from '../../../services/AuctionsService'
import SiteService from '../../../services/SiteService'
import SearchAuctions from '../../../components/Auctions/SearchAuctions'
import PageTitle from '../../../components/PageTitles/PageTitle'
import SellerInventoryTabs from '../../../components/Auctions/SellerInventoryTabs'
import SellerTabStatusFilter from '../../../components/Auctions/SellerTabStatusFilter'

const AuctionSearch = () =>
{
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = React.useState(false)
	const auctionsContext = useContext(AuctionsContext)
	const navigate = useNavigate()
	const [searchRequest, setSearchRequest] = useState({ pageIndex: 0, pageSize: 15, serialNumber: '', auctionStateId: undefined, makes: [], models: [], yearFrom: undefined, yearTo: undefined, odometerFrom: undefined, odometerTo: undefined, priceFrom: undefined, priceTo: undefined })
	const sessionContext = useContext(SessionContext)
	const [viewModel, setViewModel] = useState({ makes: [], models: [], provinces: [], vehicleYearsFrom: undefined, vehicleYearsTo: undefined })
	const [auctionStates, setAuctionStates] = useState([])
	const [showFilters, setShowFilters] = useState(false)
	
	useEffect(() =>
	{
		try
		{
			if (!auctionsContext.sellerInventoryTab)
			{
				auctionsContext.setSellerInventoryTab('auction')
			}
		}
		catch {}

		// Default UX: show listings right away (filters remain collapsed by default).
		// We persist the current searchRequest and immediately navigate to the listing view.
		// Also reset the BRD status filter dropdown so it can't desync from the empty
		// searchRequest we're about to write into context (would otherwise show "Live"
		// in the dropdown while sending statusFilter=null to the API).
		try
		{
			auctionsContext.setSearchRequest(searchRequest)
			try { auctionsContext.setSellerTabStatusFilter('all') } catch {}
			navigate('/secure/auctions-listing')
			return
		}
		catch {}

		setAuctionStates([
			{ auctionStateId: 0, name: 'All' },
			{ auctionStateId: 1, name: 'Grounded' },
			{ auctionStateId: 2, name: 'AwaitingInspection' },
			{ auctionStateId: 3, name: 'AwaitingPricing' },
			{ auctionStateId: 4, name: 'AwaitingRelease' },
			{ auctionStateId: 5, name: 'Released' },
			{ auctionStateId: 6, name: 'InSettlement' },
			{ auctionStateId: 7, name: 'Sold' },
			{ auctionStateId: 8, name: 'CompleteUnsold' },
			{ auctionStateId: 9, name: 'Expired' },
			{ auctionStateId: 10, name: 'Cancelled' },
		])
		retrieveViewModel(searchRequest)
	}, [])

	const pendingSubFilterToAuctionStateId = (subFilterKey) =>
	{
		switch (subFilterKey)
		{
			case 'all':
				return 0
			case 'grounded':
				return 1
			case 'awaiting-inspection':
				return 2
			case 'awaiting-pricing':
				return 3
			case 'awaiting-release':
				return 4
			case 'cancelled':
				return 10
			default:
				return 1
		}
	}

	const inventoryTabToAuctionStateId = (tabKey) =>
	{
		switch (tabKey)
		{
			case 'pending':
				return pendingSubFilterToAuctionStateId(auctionsContext.sellerPendingSubFilter || 'all')
			case 'auction':
				return 5
			case 'bid-review':
				return 6
			case 'sold':
				return 7
			default:
				return 0
		}
	}

	const tabStatusToAuctionStateId = (tabKey, statusKey) =>
	{
		if (!statusKey || statusKey === 'all') return inventoryTabToAuctionStateId(tabKey)

		if (tabKey === 'auction')
		{
			switch (statusKey)
			{
				case 'awaiting-inspection':
					return 2
				case 'awaiting-seller-release':
					return 4
				case 'live':
					return 5
				default:
					return inventoryTabToAuctionStateId(tabKey)
			}
		}

		if (tabKey === 'bid-review')
		{
			if (statusKey === 'bid-review') return 6
			return inventoryTabToAuctionStateId(tabKey)
		}

		if (tabKey === 'sold')
		{
			if (statusKey === 'sold') return 7
			return inventoryTabToAuctionStateId(tabKey)
		}

		// pending
		if (statusKey === 'cancelled') return 10
		if (statusKey === 'unsold')
		{
			// Unsold = CompleteUnsold (8) + Expired (9). Current API can't express multi-state.
			return 0
		}
		return inventoryTabToAuctionStateId(tabKey)
	}

	const applyAuctionStateId = (auctionStateId) =>
	{
		setSearchRequest((previousSearchRequest) =>
		{
			const currentSearchRequest = { ...previousSearchRequest, auctionStateId, pageIndex: 0 }
			retrieveViewModel(currentSearchRequest)
			return currentSearchRequest
		})
	}

	const handleInventoryTabChange = (tabKey) =>
	{
		try { auctionsContext.setSellerInventoryTab(tabKey) } catch {}
		try { auctionsContext.setSellerTabStatusFilter('all') } catch {}
		applyAuctionStateId(inventoryTabToAuctionStateId(tabKey))
	}

	const handlePendingSubFilterChange = (subFilterKey) =>
	{
		try { auctionsContext.setSellerPendingSubFilter(subFilterKey) } catch {}
		if ((auctionsContext.sellerInventoryTab || 'auction') !== 'pending') return
		applyAuctionStateId(pendingSubFilterToAuctionStateId(subFilterKey))
	}

	const handleTabStatusFilterChange = (statusKey) =>
	{
		try { auctionsContext.setSellerTabStatusFilter(statusKey) } catch {}
		const tabKey = auctionsContext.sellerInventoryTab || 'auction'
		applyAuctionStateId(tabStatusToAuctionStateId(tabKey, statusKey))
	}
	
	const retrieveViewModel = (searchRequest) =>
	{
		setIsBusy(true)
		AuctionsService
			.getBusinessSearchViewModel(searchRequest, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setViewModel(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleSearchParameterChange = (e) =>
	{
		const { name, value} = e.target
		setSearchRequest((previousSearchRequest) =>
		{
			const currentSearchRequest = { ...previousSearchRequest }
			currentSearchRequest[name] = value
			
			return currentSearchRequest
		})
	}
	
	const handleStatusChange = (e) =>
	{
		const { name, value} = e.target
		setSearchRequest((previousSearchRequest) =>
		{
			const currentSearchRequest = { ...previousSearchRequest }
			currentSearchRequest[name] = value
			
			retrieveViewModel(currentSearchRequest)
			
			return currentSearchRequest
		})
	}
	
	const handleMakesChange = (value) =>
	{
		setSearchRequest((prev) => {
			const current = prev.makes || []
			const newValues = current.includes(value)
				? current.filter(v => v !== value)
				: [...current, value]
			return { ...prev, makes: newValues }
		})
	}
	
	const handleModelsChange = (value) =>
	{
		setSearchRequest((prev) => {
			const current = prev.models || []
			const newValues = current.includes(value)
				? current.filter(v => v !== value)
				: [...current, value]
			return { ...prev, models: newValues }
		})
	}
	
	const handleProvincesChange = (value) =>
	{
		setSearchRequest((prev) => {
			const current = prev.provinces || []
			const newValues = current.includes(value)
				? current.filter(v => v !== value)
				: [...current, value]
			return { ...prev, provinces: newValues }
		})
	}
	
	
	const handleSearchAuctions = () =>
	{
		auctionsContext.setSearchRequest(searchRequest)
		navigate('/secure/auctions-listing')
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitle
				heading={ t('Auctions.Search.Title') }
				subheading=""
				icon="pe-7s-ticket icon-gradient bg-premium-dark"
			/>
			<Container fluid>
				<Card className="mb-3">
					<CardHeader>
						<SellerInventoryTabs
							activeTab={ auctionsContext.sellerInventoryTab || 'auction' }
							onTabChange={ handleInventoryTabChange }
						/>
						<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
							<Button
								color="link"
								onClick={ () => setShowFilters((s) => !s) }
								className="text-decoration-none"
								size="sm"
								style={{ paddingRight: 0, paddingLeft: 0 }}
							>
								<FontAwesomeIcon icon={ faFilter } /> { showFilters ? 'Hide Filters' : 'Show Filters' }
							</Button>
						</div>
					</CardHeader>
					<CardBody>
						{ showFilters && (
							<div>
								<div style={{ marginBottom: '0.75rem' }}>
									<SellerTabStatusFilter
										parentTab={ auctionsContext.sellerInventoryTab || 'auction' }
										value={ auctionsContext.sellerTabStatusFilter || 'all' }
										onChange={ handleTabStatusFilterChange }
									/>
								</div>
								<SearchAuctions
									viewModel={ viewModel }
									auctionStates={ auctionStates }
									handleStatusChange={ handleStatusChange }
									handleMakesChange={ handleMakesChange }
									handleModelsChange={ handleModelsChange }
									searchRequest={ searchRequest }
									handleProvincesChange={ handleProvincesChange }
									handleSearchParameterChange={ handleSearchParameterChange }
									handleSearchAuctions={ handleSearchAuctions }
								/>
							</div>
						) }
					</CardBody>
				</Card>
			</Container>
		</>
	)
}

export default AuctionSearch
